import { DateTime } from 'luxon';
import { ADJECTIVES, DATE_FORMATS } from './constants';
import { Buffer } from 'buffer';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const validateEmail = (input: string) => EMAIL_PATTERN.test(input);

const MOBILE_PATTERN = /^\+\d{1,3}[- ]?\d{1,14}([- ]?\d{1,13})*$/;
export const validatePhone = (phoneNumber: string) => MOBILE_PATTERN.test(phoneNumber);

export const formattedDate = (date: Date, format: string) => DateTime.fromJSDate(date).toFormat(format);

export const generateHexIdentifier = (length: number) =>
  [...Array(length)].map(() => Math.floor(Math.random() * 16).toString(16)).join('');

export const getRandomAdjective = () => ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];

const unscramble = (scrambled: string): string => {
  const buffer = Buffer.from(scrambled, 'hex');
  const [t0, t1, r0, r1] = [0, 6, 6, 8];
  const dr = r1 - r0;
  
  for (let i = 0; i < t1 - t0; i++) {
    buffer[t0 + i] = (0x100 + buffer[t0 + i] - buffer[r0 + (i % dr)]) % 0x100;
  }
  
  return buffer.toString('hex');
};

export const reverse = (s: string): string => s.split('').reverse().join('');

export const createEntryIdentifier = (surveyIdentifier: string): string => {
  const hexTimestamp = reverse(Date.now().toString(16)).padEnd(12, '0');
  const unscrambledSurveyIdentifier = unscramble(surveyIdentifier);
  
  return reverse(hexTimestamp) + unscrambledSurveyIdentifier + '0000';
};

export const jsToPrimeVueDateFormat = (jsFormat?: string): string | undefined => {

  if (!jsFormat) {
    return undefined;
  }

  // Define a map for JavaScript to PrimeVue formats
  const conversionMap: Record<string, string> = {
    'yyyy': 'yy',   // 4-digit year -> full year
    'yy': 'y',      // 2-digit year -> last two digits of the year
    'MMMM': 'MM',   // Full month name -> full month name (PrimeVue)
    'MMM': 'M',     // Short month name -> short month name (PrimeVue)
    'MM': 'mm',     // 2-digit month -> month with leading zero
    'M': 'm',       // 1-digit month -> month without leading zero
    'dd': 'dd',     // 2-digit day -> day with leading zero
    'd': 'd',       // 1-digit day -> day without leading zero
    'DDD': 'DD',    // Full day name -> full day name (PrimeVue)
    'D': 'D',       // Short day name -> short day name (PrimeVue)
    'ooo': 'oo',    // Day of year with leading zeros -> 3-digit day of year
    'o': 'o'       // Day of year without leading zeros -> day of year
  };

  // Replace the JS format components with PrimeVue equivalents
  return jsFormat.replace(/yyyy|yy|MMMM|MMM|MM|M|dd|d|DDD|D|ooo|o/g, (match: string) => {
    return conversionMap[match] || match;
  });
};

export function getFormattedDateList() {
  const currentDate = DateTime.now();

  return DATE_FORMATS.map(formatGroup => ({
    label: formatGroup.label,
    items: formatGroup.items.map(format => {
      return {
        label: `${format.value} (${currentDate.toFormat(format.value)})`,
        value: format.value
      };
    })
  }));
}

// Converts seconds since epoch (UTC) to a JS Date object for display purposes
export function getUTCJSDate(seconds: number) {
  return DateTime.fromSeconds(seconds, { zone: 'utc' })
    .startOf('minute') // Sets seconds and milliseconds to 0
    .setZone('local', { keepLocalTime: true })
    .toJSDate();
}

// Converts a JS Date object to seconds since epoch (UTC) to store in the database
export function getUTCSeconds(date: Date) {
  return DateTime.fromJSDate(date)
    .setZone('utc', { keepLocalTime: true })
    .toSeconds();
}

export function getUTCSecondsAtStartOfDay(date: Date) {
  return DateTime.fromJSDate(date)
    .startOf('day')
    .setZone('utc', { keepLocalTime: true })
    .toSeconds();
}

export function getOnlyTimeSeconds(date: Date) {
  const dt = DateTime.fromJSDate(date);
  const startOfDay = dt.startOf('day');
  return dt.diff(startOfDay, 'seconds').seconds;
}

export function getTimeFromSeconds(seconds: number) {
  return DateTime.now()
    .startOf('day')
    .plus({ seconds })
    .toJSDate();
}

export const getLocalTimezoneDetails = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return {
    timezone: `${timezone} ${getFormattedOffset()}`
  };
};

export const getSystemTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const getFormattedOffset = (): string => {
  const offsetMinutes = new Date().getTimezoneOffset();
  const sign = offsetMinutes > 0 ? '-' : '+';
  const hours = String(Math.floor(Math.abs(offsetMinutes) / 60)).padStart(2, '0');
  const minutes = String(Math.abs(offsetMinutes) % 60).padStart(2, '0');
  return `${sign}${hours}:${minutes}`;
};

export const getRandomRgbColor = () => {
  const randomValue = () => Math.floor(Math.random() * 256); // Generate random number between 0 and 255 for RGB values
  const r = randomValue();
  const g = randomValue();
  const b = randomValue();

  return `rgba(${r}, ${g}, ${b})`;
};

export const addOpacityToRgb = (rgbColor: string, opacity = 1) => {
  // Extract the RGB values using a regular expression
  const rgbValues = rgbColor.match(/\d+/g);
  
  if (!rgbValues || rgbValues.length < 3) {
    throw new Error('Invalid RGB color format');
  }

  // Ensure opacity is between 0 and 1
  const a = Math.max(0, Math.min(opacity, 1));

  // Return the color in RGBA format
  return `rgba(${rgbValues[0]}, ${rgbValues[1]}, ${rgbValues[2]}, ${a})`;
};

export const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const convertStringHoursToNumberHours = (timepoint?: string): number => {
  if (!timepoint) return NaN;
  const match = timepoint.match(/(-?\d+)\s*(mins|hours?)/);
  if (!match) return NaN;

  const value = parseFloat(match[1]);
  const unit = match[2];

  return unit.includes('min') ? value / 60 : value;
};

export const getHoursString = (num: number) => {
  const hours = Math.trunc(num);
  const minutes = Math.round((Math.abs(num) % 1) * 60);

  const hourLabel = Math.abs(hours) === 1 ? 'hour' : 'hours';

  if (minutes === 0) {
    return `${hours} ${hourLabel}`;
  } else if (num >= 0) {
    return `${hours > 0 ? hours + ' ' + hourLabel + ' ' : ''}${minutes} mins`;
  } else {
    return `${hours !== 0 ? hours + ' ' + hourLabel + ' ' : ''}-${minutes} mins`;
  }
};

export const roundNumber = (num: number, decimalPlaces: number, mode = 'normal') => {
  const factor = Math.pow(10, decimalPlaces);
  
  if (mode === 'up') {
    return Math.ceil(num * factor) / factor;
  } else if (mode === 'down') {
    return Math.floor(num * factor) / factor;
  } else {
    return Math.round(num * factor) / factor; // Default: Standard rounding
  }
};

export function shiftTimestampByDateShift(timestamp: number, dateShift: number) {
  return DateTime
    .fromSeconds(timestamp)
    .plus({ days: dateShift })
    .toSeconds();
}

export function reverseShiftedTimestamp(shiftedTimestamp: number, dateShift: number) {
  return DateTime
    .fromSeconds(shiftedTimestamp)
    .minus({ days: dateShift })
    .toSeconds();
}

export function elapsedTimeText(now: DateTime, lastRefreshed: DateTime) {
  const diff = now
    .diff(lastRefreshed, ['minutes', 'hours'])
    .toObject();

  if (diff.hours && diff.hours >= 1) {
    return `The dashboard was refreshed ${Math.floor(diff.hours)} hour(s) ago`;
  } else if (diff.minutes && diff.minutes >= 1) {
    return `The dashboard was refreshed ${Math.floor(
      diff.minutes
    )} minute(s) ago`;
  } else {
    return `The dashboard was refreshed just now`;
  }
}

export const DashboardGraphFilters = [
  {
    id: 0,
    label: 'All livers',
    filters: [
      {
        label: 'All data',
        value: 'all',
        key: 'All_livers',
        selected: true
      }
    ]
  },
  {
    id: 1,
    label: 'Donor type',
    filters: [
      {
        label: 'DCD',
        value: 'DCD',
        key: 'Donor_type',
        selected: false
      },
      {
        label: 'DBD',
        value: 'DBD',
        key: 'Donor_type',
        selected: false
      }
    ]
  },
  {
    id: 2,
    label: 'Donor age',
    filters: [
      {
        label: 'Younger than 60',
        value: '<60yo',
        key: 'Donor_age_group',
        selected: false
      },
      {
        label: 'Older than 60',
        value: '>=60yo',
        key: 'Donor_age_group',
        selected: false
      }
    ]
  },
  {
    id: 3,
    label: 'Steatosis',
    filters: [
      {
        label: 'Macro < 10%',
        value: '<10%',
        key: 'Donor_macrosteatosis',
        selected: false
      },
      {
        label: 'Macro 10-30%',
        value: '10-30%',
        key: 'Donor_macrosteatosis',
        selected: false
      }
    ]
  },
  {
    id: 4,
    label: 'NRP',
    filters: [
      {
        label: 'NRP - Yes',
        value: 'NRP_Yes',
        key: 'Donor_NRP',
        selected: false
      },
      {
        label: 'NRP - No',
        value: 'NRP_No',
        key: 'Donor_NRP',
        selected: false
      }
    ]
  },
  {
    id: 5,
    label: 'Transplanted',
    filters: [
      {
        label: 'Transplanted - Yes',
        value: 'Transplanted_Yes',
        key: 'Transplanted',
        selected: false
      }
    ]
  }
];

export const createRandomCaseLabel = () => {
  const timestamp = DateTime.now().toFormat('yyyyMMddHHmmss');
  return `Case_${timestamp}`;
};

export const delayedPromise = (ms: number) => new Promise<void>(resolve => setTimeout(() => resolve(), ms));

export const formatTimeSpent = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined || seconds === 0) {
    return '';
  }
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  const parts: string[] = [];
  
  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
  }
  if (secs > 0 || parts.length === 0) {
    parts.push(`${secs} ${secs === 1 ? 'second' : 'seconds'}`);
  }
  
  return parts.join(' ');
};
